<template>
  <DataTableServer
    @click:row="handlePublicRecordRowClick"
    @update:page="handlePageChange"
    @update:sort-by="handleSortChange"
    :headers="headers"
    :hide-default-footer="totalItemCount === 0"
    :items="publicRecords"
    :items-length="totalItemCount"
    :items-per-page="pageSize"
    :loading="processing"
    :page="page"
    :row-props="rowProps"
    :sort-by="sortByData"
    data-testid="license-public-records-table"
    no-data-text="No public records found"
    border
    hover
  >
    <template #[`item.id`]="{ item: publicRecord }">
      {{ shortId(publicRecord.id) }}
    </template>

    <template #[`item.type`]="{ item: publicRecord }">
      <span :data-testid="`license-public-record-type-${publicRecord.id}`">
        {{ publicRecord.type }}
      </span>
    </template>

    <template #[`item.summary`]="{ item: publicRecord }">
      <span
        v-if="publicRecord.summary"
        :data-testid="`license-public-record-summary-${publicRecord.id}`"
        class="d-inline-block text-truncate license-public-record-summary"
      >
        {{ publicRecord.summary }}
      </span>
    </template>

    <template #[`item.event_date`]="{ item: publicRecord }">
      <LongDate
        :date="publicRecord.event_date"
        short-month
      />
    </template>

    <template #[`item.severity`]="{ item: publicRecord }">
      {{ publicRecord.severity }}
    </template>

    <template #[`item.published_date`]="{ item: publicRecord }">
      <span :data-testid="`license-public-record-published-date-${publicRecord.id}`">
        <LongDate
          v-if="isPublicRecordPublished(publicRecord)"
          :date="publicRecord.published_date"
          short-month
        />
      </span>
    </template>

    <template #[`item.expiration_date`]="{ item: publicRecord }">
      <LongDate
        :date="publicRecord.expiration_date"
        short-month
      />
    </template>
  </DataTableServer>

  <PublicRecordDetailDialog ref="publicRecordDetailDialog" />
</template>

<script setup>
import { useStore } from 'vuex';

import DataTableServer from '@/shared/components/table/DataTableServer.vue';
import LongDate from '@/shared/components/LongDate.vue';
import PublicRecordDetailDialog from '@/shared/components/public-records/PublicRecordDetailDialog.vue';
import { shortId } from '@/plugins/filters';
import useApi from '@/shared/composables/useApi';
import useRole from '@/shared/composables/useRole';

const { api: Api } = useApi();
const { role } = useRole();
const store = useStore();

const publicRecords = ref([]);
const page = ref(1);
const pageSize = ref(10);
const processing = ref(false);
const totalItemCount = ref(0);
const sortField = ref(null);
const sortDir = ref(null);
const localSchemas = ref({});
const publicRecordDetailDialog = ref(null);

const schemas = computed(() => ({
  ...store.state.schemas,
  ...localSchemas.value,
}));

const sortByData = computed(() =>
  sortField.value ? [{ key: sortField.value, order: sortDir.value }] : [],
);

const props = defineProps({
  licenseId: {
    type: String,
    required: true,
  },
});

const headers = computed(() => {
  const candidateHeaders = [
    { title: 'ID', value: 'id', key: 'id', field: 'id', sortable: true },
    { title: 'Type', value: 'type', key: 'type', field: 'schema_id', sortable: true },
    { title: 'Summary', value: 'summary', key: 'summary', field: 'summary', sortable: true },
    {
      title: 'Event Date',
      value: 'event_date',
      key: 'event_date',
      field: 'event_date',
      sortable: true,
    },
    { title: 'Severity', value: 'severity', key: 'severity', field: 'severity', sortable: true },
    {
      title: 'Published date',
      value: 'published_date',
      key: 'published_date',
      field: 'published_date',
      sortable: true,
    },
    {
      title: 'Expiration date',
      value: 'expiration_date',
      key: 'expiration_date',
      field: 'expiration_date',
      sortable: true,
    },
  ];

  return candidateHeaders
    .filter((header) => canViewField(header.field))
    .map(({ field: _field, ...header }) => header);
});

function canViewField(field) {
  const schemaIds = [
    ...new Set(publicRecords.value.map((record) => record.schema_id).filter(Boolean)),
  ];

  if (schemaIds.length === 0) return true;

  // Show the column when at least one record type on the page exposes the field
  // so mixed-schema pages don't over-hide
  return schemaIds.some((schemaId) => {
    const viewRoles = schemas.value[schemaId]?.definition?.properties?.[field]?.viewRoles;
    if (!Array.isArray(viewRoles)) return true;

    return viewRoles.includes(role);
  });
}

function isPublicRecordPublished(record) {
  return Boolean(record.published_date);
}

function publicRecordSchemaName(record) {
  if (record.schema_name) return record.schema_name;

  return schemas.value[record.schema_id]?.name ?? 'Unknown schema';
}

function preparePublicRecords(records) {
  return records.map((record) => ({
    ...record,
    type: publicRecordSchemaName(record),
  }));
}

async function loadMissingSchemas(records) {
  const schemaIdsNeedingLoad = [
    ...new Set(
      records
        .map((record) => record.schema_id)
        .filter(Boolean)
        .filter((schemaId) => {
          const schema = schemas.value[schemaId];
          return !schema?.name || !schema?.definition?.properties;
        }),
    ),
  ];

  await Promise.all(
    schemaIdsNeedingLoad.map(async (schemaId) => {
      const response = await Api.publicApi.organization.schema.get(schemaId);
      if (!response?.data) return;

      localSchemas.value = {
        ...localSchemas.value,
        [schemaId]: response.data,
      };
      store.commit('setSchema', response.data);
    }),
  );
}

function rowProps({ item: publicRecord }) {
  return {
    'data-testid': `license-public-record-row-${publicRecord.id}`,
    class: 'license-public-record-row',
  };
}

function handlePublicRecordRowClick(_event, { item: publicRecord }) {
  void publicRecordDetailDialog.value?.open(publicRecord);
}

function handlePageChange(newPage) {
  page.value = newPage;
  void loadPublicRecords();
}

function handleSortChange(sorts) {
  // Note: the table only supports a single sort column at a time.
  if (sorts.length === 0) {
    sortField.value = null;
    sortDir.value = null;
  } else {
    sortField.value = sorts[0].key;
    sortDir.value = sorts[0].order;
  }

  page.value = 1;
  void loadPublicRecords();
}

function load() {
  page.value = 1;
  void loadPublicRecords();
}

async function loadPublicRecords() {
  processing.value = true;
  try {
    const response = await Api.shared.publicRecord.index({
      license_id: props.licenseId,
      page: page.value,
      page_size: pageSize.value,
      sort_field: sortField.value,
      sort_dir: sortDir.value,
    });
    const records = response?.data ?? [];

    await loadMissingSchemas(records);
    publicRecords.value = preparePublicRecords(records);

    const parsedTotalCount = parseInt(response?.headers?.['x-total-count'] ?? '', 10);
    const parsedPageSize = parseInt(response?.headers?.['x-page-size'] ?? '', 10);

    totalItemCount.value = Number.isNaN(parsedTotalCount) ? records.length : parsedTotalCount;

    if (!Number.isNaN(parsedPageSize) && parsedPageSize > 0) {
      pageSize.value = parsedPageSize;
    }
  } finally {
    processing.value = false;
  }
}

onMounted(load);
</script>

<style scoped>
.license-public-record-summary {
  max-width: 230px;
  vertical-align: bottom;
}

:deep(.license-public-record-row) {
  cursor: pointer;
}
</style>
